import {FrameAction, FrameActions} from '@mc/react-frame';
import {EditorToolType, FeatureIdType, FeatureZoom, ExternalType} from '../enums';
import {
    FeatureCenterOptions,
    FeatureId,
    FeatureLoadOptions,
    FeatureMergeOptions,
    FeaturePointer,
    FeaturePointerOptions,
    FeatureRestrictOptions,
    FeatureZoomOptions,
    PaddingOptions,
    ToolActivationParams,
    ZoomOptions,
    ExternalLayerOptions,
    LayerId,
} from '../types';
import {Feature} from 'geojson';
import {valueOrDefault} from '../utils';
import {Dispatch} from 'react';

/**
 * Akce specifické pro mapový editor.
 */
export interface EditorActions {
    /**
     * Načte features do "vkládací" vrstvy editoru.
     *
     * @param features - Seznam features pro načtení.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      zoom: FeatureZoom.CENTER_AND_ZOOM
     *                  }.
     * @returns Akce.
     */
    loadFeatures: (
        features: Feature[],
        options?: FeatureZoomOptions & FeatureCenterOptions & FeatureMergeOptions,
        layerId?: LayerId,
    ) => void;

    /**
     *
     * @param externalType - typ načtení externích dat
     * @param options - pro ExternalType.WMS - url, proxy a  pro ExternalType.WFS - layers
     * @returns
     */
    loadExternalLayers: (externalType: ExternalType, options: ExternalLayerOptions) => void;

    /**
     * Odnačte features ze "vkládací" vrstvy editoru.
     *
     * @param featureIds - ID features pro odnačtení, `undefined` pro odnačtení všeho.
     * @returns Akce.
     */
    unloadFeatures: (featureIds?: FeatureId[]) => void;

    /**
     * Vybere features s danými ID.
     * Předání prázdného seznamu zruší aktuální výběr.
     *
     * @param featurePointers - Seznam ukazatelů na features pro výběr.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID,
     *                      loadMissing: true,
     *                      showDetail: true,
     *                      zoom: FeatureZoom.CENTER_AND_ZOOM
     *                  }.
     * @returns Akce.
     */
    selectFeatures: (featurePointers: FeaturePointer[], options?: FeaturePointerOptions) => void;

    /**
     *
     * @param featurePointer - Ukazatel na feature na zvýraznění
     * @param hover - pokud se na feature pouze najelo myší
     * @returns Akce
     */
    highlightFeature: (featurePointer?: FeaturePointer, hover?: boolean) => void;
    /**
     * Omezí viditelnost pouze na features s danými ID.
     *
     * @param featurePointers - Seznam ukazatelů na features, jež mají zůstat viditelné.
     *                          `null` pro zrušení restrikce.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID
     *                  }.
     * @returns Akce.
     */
    restrictFeatures: (
        featurePointers: FeaturePointer[] | null,
        options?: FeaturePointerOptions & FeatureRestrictOptions,
    ) => void;

    /**
     * Smaže features s danými ID.
     *
     * @param featurePointers - Seznam ukazatelů na features pro smazání.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID
     *                  }.
     * @returns Akce.
     */
    deleteFeatures: (featurePointers: FeaturePointer[], options?: FeaturePointerOptions) => void;

    /**
     * Provede zoom na features s danými ID.
     *
     * @param featurePointers - Seznam ukazatelů na features pro zoom.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID,
     *                      loadMissing: true
     *                  }.
     * @returns Akce.
     */
    zoomOnFeatures: (
        featurePointers: FeaturePointer[],
        options?: FeaturePointerOptions & FeatureLoadOptions & FeatureCenterOptions & ZoomOptions,
    ) => void;

    /**
     * Provede vycentrování na features s danými ID.
     *
     * @param featurePointers - Seznam ukazatelů na features pro vycentrování.
     * @param options - Volitelné parametry s výchozími hodnotami
     *                  {
     *                      featureIdType: FeatureIdType.FEATURE_ID,
     *                      loadMissing: true
     *                  }.
     * @returns Akce.
     */
    centerOnFeatures: (
        featurePointers: FeaturePointer[],
        options?: FeaturePointerOptions & FeatureLoadOptions & FeatureCenterOptions & PaddingOptions,
    ) => void;

    /**
     * Aktivuje nástroj s danými ID.
     *
     * @param toolId - ID nástroje.
     * @param toolParams - Dodatečné parametry pro aktivaci nástroje.
     * @returns Akce.
     */
    activateToolWithId: (toolId: string, toolParams?: ToolActivationParams) => void;

    /**
     * Aktivuje nástroj s danými typem.
     *
     * @param toolType - Typ nástroje.
     * @param toolParams - Dodatečné parametry pro aktivaci nástroje.
     * @returns Akce.
     */
    activateToolWithType: (toolType: EditorToolType, toolParams?: ToolActivationParams) => void;

    /**
     * Pošle event pro daný nástroj pokud je třeba dodatečná komunikace s Mapiem
     * @param toolType
     * @param toolParams
     * @returns Akce
     */
    sendToolEvent: (toolType: EditorToolType, toolParams: ToolActivationParams) => void;

    /**
     * Pošle událost zoomování mapy.
     *
     * @param zoomIncrease - Určuje, zda se má zoom zvýšit (`true`) nebo snížit (`false`).
     * @param zoomValue - Hodnota zoomu, výchozí hodnota je 1.
     * @param zoomDuration - Doba trvání zoomu v milisekundách, výchozí hodnota je 250 ms.
     * @returns Akce.
     */
    mapZoomEvent: (zoomIncrease: boolean, zoomValue?: number, zoomDuration?: number) => void;
}

/**
 * Vytvoří akce specifické pro mapový editor.
 *
 * @param dispatch - Dispatcher akcí.
 * @return Implementace akcí.
 * @ignore
 */
export const createEditorActions = (dispatch: Dispatch<FrameAction>): EditorActions => ({
    loadFeatures: (
        features: Feature[],
        options: FeatureZoomOptions & FeatureCenterOptions & FeatureMergeOptions = {},
        layerId?: LayerId,
    ): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_LOAD',
                layerId,
                features,
                zoom: valueOrDefault(options.zoom, FeatureZoom.CENTER_AND_ZOOM),
                zoomPadding: options.padding,
                zoomRatio: options.zoomRatio,
                zoomMinScale: options.zoomMinScale,
                useAsOrigin: options.useAsOrigin,
                merge: options.merge,
            }),
        ),
    loadExternalLayers: (externalType: ExternalType, options: ExternalLayerOptions): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'EXTERNAL_LAYER_LOAD',
                externalType,
                options,
            }),
        ),
    unloadFeatures: (featureIds?: FeatureId[]): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_UNLOAD',
                featureIds,
            }),
        ),

    selectFeatures: (featurePointers: FeaturePointer[], options: FeaturePointerOptions = {}): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_SELECT',
                featurePointers,
                featureIdType: valueOrDefault(options.featureIdType, FeatureIdType.FEATURE_ID),
                loadMissing: valueOrDefault(options.loadMissing, true),
                showDetail: valueOrDefault(options.showDetail, true),
                zoom: options.zoom,
                zoomPadding: options.padding,
                zoomRatio: options.zoomRatio,
                zoomMinScale: options.zoomMinScale,
                useAsOrigin: options.useAsOrigin,
                tags: options.tags,
            }),
        ),
    highlightFeature: (featurePointer?: FeaturePointer, hover?: boolean): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_HIGHLIGHT',
                featurePointer,
                hover,
            }),
        ),

    restrictFeatures: (
        featurePointers: FeaturePointer[] | null,
        options: FeaturePointerOptions & FeatureRestrictOptions = {},
    ): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_RESTRICT',
                featurePointers,
                featureIdType: valueOrDefault(options.featureIdType, FeatureIdType.FEATURE_ID),
                description: options.description,
            }),
        ),

    deleteFeatures: (featurePointers: FeaturePointer[], options: FeaturePointerOptions = {}): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_DELETE',
                featurePointers,
                featureIdType: valueOrDefault(options.featureIdType, FeatureIdType.FEATURE_ID),
            }),
        ),

    zoomOnFeatures: (featurePointers: FeaturePointer[], options: FeaturePointerOptions = {}): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_ZOOM',
                featurePointers,
                featureIdType: valueOrDefault(options.featureIdType, FeatureIdType.FEATURE_ID),
                loadMissing: valueOrDefault(options.loadMissing, true),
                zoomPadding: options.padding,
                zoomRatio: options.zoomRatio,
                zoomMinScale: options.zoomMinScale,
                useAsOrigin: options.useAsOrigin,
            }),
        ),

    centerOnFeatures: (featurePointers: FeaturePointer[], options: FeaturePointerOptions = {}): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'FEATURE_CENTER',
                featurePointers,
                featureIdType: valueOrDefault(options.featureIdType, FeatureIdType.FEATURE_ID),
                loadMissing: valueOrDefault(options.loadMissing, true),
                useAsOrigin: options.useAsOrigin,
                padding: options.padding,
            }),
        ),

    activateToolWithId: (toolId: string, toolParams?: ToolActivationParams): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'TOOL_ACTIVATION',
                toolId,
                toolParams,
            }),
        ),

    activateToolWithType: (toolType: EditorToolType, toolParams?: ToolActivationParams): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'TOOL_ACTIVATION',
                toolType,
                toolParams,
            }),
        ),

    sendToolEvent: (toolType: EditorToolType, toolParams: ToolActivationParams): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'TOOL_EVENT_PARAMETERS',
                toolType,
                toolParams,
            }),
        ),
    mapZoomEvent: (zoomIncrease: boolean, zoomValue?: number, zoomDuration?: number): void =>
        dispatch(
            FrameActions.postMessage({
                type: 'MAP_ZOOM_EVENT',
                zoomIncrease,
                zoomValue: zoomValue ?? 1,
                zoomDuration: zoomDuration ?? 250,
            }),
        ),
});
